// frontend/src/services/share.ts
//
// One OS-share-sheet entry point for everything that leaves the app (league
// invites, claim links, contest shares). WKWebView does NOT expose
// navigator.share, so on native the buttons were silently falling back to
// clipboard copy — the Capacitor Share plugin is the real share sheet there.
// Web keeps the Web Share API where present.
//
// Callers keep their own clipboard fallbacks: check shareSheetAvailable()
// first (in handlers AND render gates — never navigator.share directly),
// treat a `false` result as "user dismissed the sheet", and treat a THROW as
// "no working share sheet after all" (plugin missing from the installed
// binary, import failure) — that's the case to fall back to clipboard on.
import { isNativePlatform } from '@/services/platform';

export function shareSheetAvailable(): boolean {
  return isNativePlatform() || typeof navigator.share === 'function';
}

function isShareCancel(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  // Web Share API rejects with AbortError on dismiss; the Capacitor plugin
  // rejects with a "Share canceled" message.
  return e?.name === 'AbortError' || /cancel/i.test(e?.message ?? '');
}

export async function openShareSheet(opts: { title: string; text?: string; url: string }): Promise<boolean> {
  try {
    if (isNativePlatform()) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title: opts.title, text: opts.text, url: opts.url });
    } else {
      await navigator.share(opts);
    }
    return true;
  } catch (err) {
    if (isShareCancel(err)) return false;
    throw err;
  }
}
